import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BRAND = {
  purple: "#7C4DFF",
  gradient: "linear-gradient(180deg, #8862F2 0%, #7544FC 29%, #40248B 100%)",
};

const SECTIONS = [
  { id: "intro", label: "Home" },
  { id: "contacts", label: "Features" },
  { id: "privacy", label: "Privacy" },
  { id: "start", label: "Start" },
];

function SectionTag({ children }) {
  return (
    <span className="inline-block rounded-md bg-violet-100/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-violet-700">
      {children}
    </span>
  );
}

function SectionHeader({ tag, title, subtitle }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <SectionTag>{tag}</SectionTag>
      <h2 className="mt-5 text-3xl font-bold tracking-tight text-[#212121] sm:text-4xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#757575] sm:text-lg">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function PrimaryBtn({ to, children, className = "" }) {
  return (
    <Link
      to={to}
      className={`submit-button inline-flex items-center justify-center gap-2 text-sm font-semibold !rounded-full shadow-lg shadow-violet-600/25 transition hover:brightness-105 active:scale-[0.98] ${className}`}
    >
      {children}
    </Link>
  );
}

function OutlineBtn({ to, children, className = "" }) {
  return (
    <Link
      to={to}
      className={`normal-button inline-flex items-center justify-center text-sm font-semibold !rounded-full !px-8 !py-3.5 !shadow-md transition hover:bg-violet-50 active:scale-[0.98] ${className}`}
    >
      {children}
    </Link>
  );
}

const DEMO_CONTACTS = [
  { name: "Priya Sharma", tag: "Added Contact", initial: "P", hue: "from-violet-500 to-purple-600" },
  { name: "Rahul Mehta", tag: "Work", initial: "R", hue: "from-indigo-500 to-blue-600" },
  { name: "Ananya Patel", tag: "Family", initial: "A", hue: "from-fuchsia-500 to-pink-600" },
];

function ContactRow({ contact, compact = false }) {
  const avatar = compact ? "h-9 w-9 text-xs" : "h-10 w-10 text-sm";
  return (
    <div className="flex items-center gap-2.5 rounded-xl px-2 py-2 hover:bg-violet-50/60">
      <div
        className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-bold text-white ${avatar} ${contact.hue}`}
      >
        {contact.initial}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`truncate font-semibold text-gray-900 ${compact ? "text-xs" : "text-[13px]"}`}>
          {contact.name}
        </p>
        <p className={`font-medium text-violet-600 ${compact ? "text-[9px]" : "text-[10px]"}`}>
          {contact.tag}
        </p>
      </div>
      <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-emerald-700 sm:text-[9px]">
        Synced
      </span>
    </div>
  );
}

function WebMockup() {
  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_24px_60px_-20px_rgba(64,36,139,0.35)] ring-1 ring-black/5">
        {/* browser chrome */}
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="mx-auto flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
            <svg className="h-3.5 w-3.5 shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="truncate text-[10px] text-gray-500 sm:text-xs">
              phonebookplus.app/contact
            </span>
          </div>
        </div>

        <div className="flex min-h-[200px] sm:min-h-[240px]">
          {/* sidebar */}
          <div className="hidden w-[72px] shrink-0 flex-col border-r border-gray-100 bg-[#FAFAFA] py-3 sm:flex sm:w-20">
            <div className="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-lg shadow-sm" style={{ background: BRAND.gradient }}>
              <img src="/logo.svg" alt="" className="h-4 w-4" />
            </div>
            {[
              { label: "Contacts", active: true },
              { label: "Chat", active: false },
              { label: "Groups", active: false },
            ].map((item) => (
              <div
                key={item.label}
                className={`mx-2 mb-1 rounded-lg px-1 py-2 text-center text-[9px] font-medium ${
                  item.active ? "bg-violet-100 text-violet-700" : "text-gray-400"
                }`}
              >
                {item.label}
              </div>
            ))}
          </div>

          {/* main */}
          <div className="min-w-0 flex-1">
            <div className="border-b border-gray-100 px-3 py-3 sm:px-4" style={{ background: BRAND.gradient }}>
              <div className="flex items-center justify-between text-white">
                <div>
                  <p className="text-sm font-bold sm:text-base">My Contacts</p>
                  <p className="text-[10px] text-white/75 sm:text-xs">PhonebookPlus Web</p>
                </div>
                <button type="button" className="rounded-lg bg-white/20 px-2.5 py-1 text-[10px] font-semibold sm:text-xs">
                  + Add
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-white/20 px-2.5 py-2">
                <svg className="h-3.5 w-3.5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-[10px] text-white/70 sm:text-xs">Search contacts...</span>
              </div>
            </div>
            <div className="space-y-0.5 bg-white p-1.5 sm:p-2">
              {DEMO_CONTACTS.map((c) => (
                <ContactRow key={c.name} contact={c} compact />
              ))}
            </div>
            <div className="border-t border-violet-100 bg-violet-50/80 px-3 py-2 text-center">
              <p className="text-[10px] font-semibold text-violet-700 sm:text-xs">248+ contacts synced</p>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-2 text-center text-xs font-semibold text-violet-700">Web App</p>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="w-full max-w-[280px] mx-auto sm:max-w-[300px]">
      <div className="rounded-[2.5rem] bg-gray-900 p-[6px] shadow-[0_28px_56px_-14px_rgba(64,36,139,0.5)] ring-1 ring-black/10">
        <div className="overflow-hidden rounded-[2.1rem] bg-white">
          <div className="relative flex items-center justify-between bg-white px-4 pb-1 pt-2.5">
            <span className="text-[10px] font-semibold text-gray-900">9:41</span>
            <div className="absolute left-1/2 top-1.5 h-5 w-[76px] -translate-x-1/2 rounded-full bg-gray-900" />
            <div className="h-2 w-3 rounded-sm bg-gray-900" />
          </div>
          <div className="px-3 pb-3 pt-0.5 text-white" style={{ background: BRAND.gradient }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold">My Contacts</p>
                <p className="text-[10px] text-white/75">PhonebookPlus</p>
              </div>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-white/20 px-2.5 py-2">
              <svg className="h-3.5 w-3.5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-[10px] text-white/70">Search...</span>
            </div>
          </div>
          <div className="space-y-0.5 bg-white px-1.5 py-1.5">
            {DEMO_CONTACTS.map((c) => (
              <ContactRow key={c.name} contact={c} compact />
            ))}
          </div>
          <div className="border-t border-violet-100 bg-violet-50/80 px-3 py-2.5 text-center">
            <p className="text-[10px] font-semibold text-violet-700">248+ synced</p>
          </div>
        </div>
      </div>
      <p className="mt-2 text-center text-xs font-semibold text-violet-700">Mobile App</p>
    </div>
  );
}

function PlatformShowcase() {
  return (
    <div className="relative mx-auto w-full max-w-[580px] lg:max-w-none">
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(124,77,255,0.25) 0%, transparent 70%)" }}
      />

      {/* Desktop + mobile layout */}
      <div className="relative flex flex-col gap-6 lg:block lg:min-h-[380px]">
        {/* Web — back on desktop, top on mobile */}
        <div className="relative z-0 w-full lg:absolute lg:left-0 lg:top-0 lg:w-[calc(100%-140px)] lg:max-w-[420px]">
          <WebMockup />
        </div>

        {/* Phone — front on desktop, bottom on mobile */}
        <div className="relative z-10 flex justify-center lg:absolute lg:right-0 lg:bottom-0 lg:justify-end">
          <PhoneMockup />
        </div>
      </div>

      {/* floating badges */}
      <div className="absolute left-0 top-8 z-20 hidden rounded-2xl border border-gray-100 bg-white p-3 shadow-xl lg:block">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-900">Sync complete</p>
            <p className="text-[10px] text-gray-500">Web &amp; mobile</p>
          </div>
        </div>
      </div>

      <div className="absolute right-0 top-1/2 z-20 hidden -translate-y-1/2 rounded-2xl border border-violet-100 bg-white px-3 py-2 shadow-lg lg:block">
        <p className="text-[11px] font-bold text-violet-700">Privacy</p>
        <p className="text-[10px] text-gray-500">Protected</p>
      </div>

      {/* platform pills — mobile */}
      <div className="mt-4 flex justify-center gap-2 lg:hidden">
        <span className="rounded-full border border-violet-200 bg-white px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm">
          Web App
        </span>
        <span className="rounded-full border border-violet-200 bg-white px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm">
          Mobile App
        </span>
      </div>
    </div>
  );
}

function TrustStrip() {
  const items = [
    {
      title: "Sync",
      sub: "Address book",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
        </svg>
      ),
    },
    {
      title: "Secure",
      sub: "Privacy first",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
    },
    {
      title: "Global",
      sub: "Worldwide",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
    },
  ];

  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-violet-100/90 bg-white shadow-[0_4px_24px_-8px_rgba(124,77,255,0.15)]">
      <div className="grid grid-cols-3 divide-x divide-violet-100">
        {items.map((item) => (
          <div key={item.title} className="flex flex-col items-center px-3 py-4 text-center sm:flex-row sm:gap-3 sm:px-5 sm:text-left">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
              style={{ background: BRAND.gradient }}
            >
              {item.icon}
            </div>
            <div className="mt-2 sm:mt-0">
              <p className="text-sm font-bold" style={{ color: BRAND.purple }}>
                {item.title}
              </p>
              <p className="text-[11px] text-gray-500">{item.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FEATURE_ICONS = {
  sync: [
    "M16.023 9.348h4.992M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99",
  ],
  tiers: [
    "M3.75 7.5h16.5",
    "M3.75 12h16.5",
    "M3.75 16.5h16.5",
  ],
  visibility: [
    "M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12z",
    "M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  ],
  document: [
    "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0013.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
  ],
};

function FeatureIcon({ name, className = "h-6 w-6" }) {
  const paths = FEATURE_ICONS[name];
  if (!paths?.length) return null;
  return (
    <svg
      className={`block shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

function IconBox({ name }) {
  return (
    <div
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-md shadow-violet-500/25"
      style={{ background: BRAND.gradient }}
    >
      <FeatureIcon name={name} className="h-7 w-7" />
    </div>
  );
}

function FeatureBlock({ title, desc, iconName }) {
  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:border-violet-200 hover:shadow-[0_16px_48px_-12px_rgba(124,77,255,0.2)]">
      <div
        className="h-1 w-full scale-x-0 transition-transform duration-300 group-hover:scale-x-100"
        style={{ background: BRAND.gradient }}
        aria-hidden
      />
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <IconBox name={iconName} />
        <h3 className="mt-5 text-lg font-bold leading-snug text-gray-900 sm:text-xl">
          {title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600 sm:text-[15px]">
          {desc}
        </p>
      </div>
    </article>
  );
}

function FooterColumn({ title, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-300">
        {title}
      </p>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function SiteFooter({ onSectionClick }) {
  const year = new Date().getFullYear();

  const productLinks = [
    { label: "Home", action: () => onSectionClick(0) },
    { label: "Features", action: () => onSectionClick(1) },
    { label: "Privacy", action: () => onSectionClick(2) },
    { label: "About Us", action: () => onSectionClick(3) },
  ];

  const linkClass =
    "text-sm text-violet-100/90 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#2d1a5c] rounded-sm";

  return (
    <footer className="relative z-10 w-full border-t border-violet-400/15 bg-[#1e1045] pb-24 text-white xl:pb-10">
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12">
          {/* Brand */}
          <div className="min-w-0 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-lg ring-1 ring-white/10"
                style={{ background: BRAND.gradient }}
              >
                <img src="/logo.svg" alt="" className="h-6 w-6" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                PhoneBook<span className="text-violet-300">Plus</span>
              </span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-violet-200/85">
              Smart contact directory — sync, organize, and connect securely. Built for users worldwide.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["Web", "Mobile", "Global"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-violet-400/25 bg-violet-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Product */}
          <FooterColumn title="Product">
            <ul className="space-y-2.5">
              {productLinks.map((item) => (
                <li key={item.label}>
                  <button type="button" onClick={item.action} className={`${linkClass} text-left`}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </FooterColumn>

          {/* Company */}
          <FooterColumn title="Company">
            <ul className="space-y-3 text-sm text-violet-100/90">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                <span>Privacy-first by design</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                <span>Available worldwide</span>
              </li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <a
                href="#"
                className="inline-flex items-center gap-2 rounded-xl border border-violet-400/25 bg-violet-500/10 px-3 py-2 transition hover:border-violet-300/40 hover:bg-violet-500/20"
                aria-label="Get  PhoneBookPlus on Google Play"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7C4DFF]">
                  <img src="/playstore.svg" alt="" aria-hidden="true" className="h-5 w-5" />
                </span>
                <span className="text-xs font-semibold text-violet-50">Play Store</span>
              </a>
              <a
                href="#"
                className="inline-flex items-center gap-2 rounded-xl border border-violet-400/25 bg-violet-500/10 px-3 py-2 transition hover:border-violet-300/40 hover:bg-violet-500/20"
                aria-label="Get  PhoneBookPlus on the App Store"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7C4DFF]">
                  <img src="/appstore.svg" alt="" aria-hidden="true" className="h-5 w-5" />
                </span>
                <span className="text-xs font-semibold text-violet-50">App Store</span>
              </a>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-violet-300/70">
              PhonebookPlus Technology &amp; Consulting (OPC) Pvt. Ltd.
            </p>
          </FooterColumn>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-violet-400/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-center text-xs text-violet-400/80 sm:text-left">
            © {year} PhonebookPlus. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-violet-400/80 sm:justify-end">
            <Link to="/term-condition" className="transition-colors hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/term-condition" className="transition-colors hover:text-white">
              Terms of Service
            </Link>
            <Link to="/about-us" className="transition-colors hover:text-white">
              About Us
            </Link>
            <span className="text-violet-500/60" aria-hidden="true">
              ·
            </span>
            <span>Worldwide</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    document.title = "PhonebookPlus";
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const i = Math.round(el.scrollTop / el.clientHeight);
      setActiveIndex(Math.min(i, SECTIONS.length - 1));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: i * el.clientHeight, behavior: "smooth" });
  };

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#F3F0FF] text-[#212121]">
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-violet-100/60 bg-[#F3F0FF]/90 backdrop-blur-xl pt-[env(safe-area-inset-top,0px)]">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-[72px] sm:gap-4 sm:px-5 md:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3">
            <div
              className="flex shrink-0 items-center justify-center"
            >
              <img src="/logo.svg" alt="" className="h-9 w-9 sm:h-7 sm:w-7 md:h-11 md:w-11" />
            </div>
            <span className="heading-font truncate text-base !font-bold sm:!text-xl md:!text-2xl">
              PhoneBook<span style={{ color: BRAND.purple }}>Plus</span>
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <Link
              to="/login"
              className="normal-button inline-flex items-center justify-center whitespace-nowrap !rounded-full !border-violet-300 !px-3 !py-2 text-xs font-semibold !text-violet-700 !shadow-sm hover:!bg-violet-50 sm:!px-5 sm:!py-2.5 sm:text-sm"
            >
              Sign In
            </Link>
            <PrimaryBtn to="/sign-up" className="!px-3 !py-2 inline-flex text-xs sm:!px-5 sm:!py-2.5 sm:text-sm">
              Sign Up
            </PrimaryBtn>
          </div>
        </div>
      </header>

      {/* Section nav */}
      <aside className="fixed right-6 top-1/2 z-50 hidden -translate-y-1/2 flex-col items-end gap-5 xl:flex">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(i)}
            className="group flex items-center gap-3"
            aria-label={s.label}
          >
            <span
              className={`text-[11px] font-semibold tracking-wide transition ${
                activeIndex === i ? "text-violet-700" : "text-gray-400 opacity-0 group-hover:opacity-100"
              }`}
            >
              {s.label}
            </span>
            <span
              className={`rounded-full transition-all duration-300 ${
                activeIndex === i
                  ? "h-2.5 w-8 bg-violet-600"
                  : "h-2 w-2 bg-violet-300 group-hover:bg-violet-400"
              }`}
            />
          </button>
        ))}
      </aside>

      <main
        ref={containerRef}
        className="h-[100dvh] snap-y snap-mandatory overflow-y-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {/* ═══ 1. HERO ═══ */}
        <section className="relative flex min-h-[100dvh] snap-start snap-always items-center overflow-hidden bg-[#F3F0FF]">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-32 top-0 h-[min(80vh,520px)] w-[min(80vh,520px)] rounded-full bg-violet-400/20 blur-[100px]" />
            <div className="absolute -left-24 bottom-0 h-[min(60vh,400px)] w-[min(60vh,400px)] rounded-full bg-indigo-300/25 blur-[80px]" />
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage: `radial-gradient(${BRAND.purple} 1px, transparent 1px)`,
                backgroundSize: "24px 24px",
              }}
            />
          </div>

          <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-3 pb-28 pt-[calc(3.5rem+env(safe-area-inset-top,0px)+1rem)] sm:px-5 sm:pt-[calc(4.5rem+1.5rem)] lg:grid-cols-[1fr_minmax(320px,580px)] lg:gap-12 lg:px-8 lg:pb-20 lg:pt-[100px]">
            <div className="lg:max-w-[540px]">
              <SectionTag>Welcome</SectionTag>

              <h1 className="mt-6 text-[2.125rem] font-bold leading-[1.12] tracking-tight text-[#212121] sm:text-[2.75rem] lg:text-[3.125rem]">
                Your contacts.
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: "linear-gradient(135deg, #7C4DFF 0%, #7544FC 45%, #5b3fd4 100%)",
                  }}
                >
                  Smarter &amp; secure.
                </span>
              </h1>

              <p className="mt-5 max-w-[480px] text-[15px] leading-[1.7] text-[#757575] sm:text-lg sm:leading-relaxed">
                Use PhonebookPlus on web or mobile — sync your address book, build rich profiles, and stay connected. Available worldwide with privacy-first security.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryBtn to="/sign-up" className="w-full justify-center sm:w-auto">
                  Get Started Free
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </PrimaryBtn>
                <OutlineBtn to="/login" className="w-full justify-center sm:w-auto">
                  Sign In
                </OutlineBtn>
              </div>

              <TrustStrip />
            </div>

            <div className="mt-4 lg:mt-0">
              <PlatformShowcase />
            </div>
          </div>

          {activeIndex === 0 && (
            <button
              type="button"
              onClick={() => scrollTo(1)}
              className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-1 text-violet-600/60 hover:text-violet-700 lg:hidden"
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest">Swipe up</span>
              <svg className="h-4 w-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </section>

        {/* ═══ 2. FEATURES ═══ */}
        <section className="relative flex min-h-[100dvh] snap-start snap-always items-center overflow-hidden bg-gradient-to-b from-[#F3F0FF] via-white to-[#F3F0FF]">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-20 top-1/4 h-72 w-72 rounded-full bg-violet-200/25 blur-3xl" />
            <div className="absolute -right-20 bottom-1/4 h-72 w-72 rounded-full bg-indigo-200/20 blur-3xl" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-7xl px-3 py-20 pt-[calc(3.5rem+env(safe-area-inset-top,0px)+2rem)] sm:px-5 sm:pt-[100px] lg:px-8">
            <SectionHeader
              tag="Features"
              title="Everything you need to manage contacts"
              subtitle="Powerful tools to sync, organize, and connect — without the clutter."
            />

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3 lg:gap-6">
              <FeatureBlock
                iconName="sync"
                title="Address book sync"
                desc="One-tap sync from your device. Instantly match contacts already on PhonebookPlus."
              />
              <FeatureBlock
                iconName="tiers"
                title="Smart contact tiers"
                desc="Control visibility with Added vs. stored contacts. Share only what you choose."
              />
              <div className="sm:col-span-2 lg:col-span-1">
                <FeatureBlock
                  iconName="document"
                  title="Rich profiles"
                  desc="Work, education, notes, voice messages — build a complete picture of every contact."
                />
              </div>
            </div>
          </div>
        </section>

        {/* ═══ 3. PRIVACY ═══ */}
        <section className="relative flex min-h-[100dvh] snap-start snap-always items-center bg-[#F3F0FF]">
          <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-3 py-20 pt-[calc(3.5rem+env(safe-area-inset-top,0px)+2rem)] sm:px-5 sm:pt-[100px] lg:grid-cols-2 lg:gap-20 lg:px-8">
            <div className="order-2 lg:order-1">
              <div className="lg:max-w-lg">
                <SectionTag>Privacy</SectionTag>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-[#212121] sm:text-4xl">
                  Your data. Your rules.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-[#757575] sm:text-lg">
                  Built with privacy at the core. Control what you share, with whom, and when — wherever you are.
                </p>
              </div>

              <ul className="mt-8 space-y-4">
                {[
                  "User-controlled profile visibility",
                  "Secure sync across countries and devices",
                  "In-app privacy dashboard & grievance redressal",
                ].map((text) => (
                  <li key={text} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
                      style={{ background: BRAND.gradient }}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-800">{text}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/term-condition"
                className="mt-8 inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                style={{ color: BRAND.purple }}
              >
                Read Privacy Policy
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            <div className="order-1 lg:order-2">
              <div className="mx-auto max-w-md rounded-3xl border border-violet-100 bg-white p-8 shadow-xl shadow-violet-500/10">
                <div className="flex items-center gap-4 border-b border-gray-100 pb-6">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl text-white"
                    style={{ background: BRAND.gradient }}
                  >
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Privacy Dashboard</p>
                    <p className="text-sm text-gray-500">Manage visibility per field</p>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  {[
                    { label: "Phone number", on: true },
                    { label: "Email address", on: false },
                    { label: "Work & education", on: true },
                    { label: "Location", on: false },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between rounded-xl bg-[#F8F6FF] px-4 py-3"
                    >
                      <span className="text-sm font-medium text-gray-800">{row.label}</span>
                      <div
                        className={`relative h-7 w-12 rounded-full transition ${row.on ? "bg-violet-600" : "bg-gray-200"}`}
                      >
                        <div
                          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
                            row.on ? "left-6" : "left-1"
                          }`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ 4. CTA + FOOTER ═══ */}
        <section className="relative flex min-h-[100dvh] snap-start snap-always flex-col overflow-hidden">
          {/* CTA area */}
          <div className="relative flex flex-1 flex-col">
            <div className="absolute inset-0" style={{ background: BRAND.gradient }} />
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: "32px 32px",
              }}
            />
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="absolute -left-20 top-1/4 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
              <div className="absolute -right-20 top-1/3 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />
            </div>

            <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-3 py-16 pt-[calc(3.5rem+env(safe-area-inset-top,0px)+2rem)] text-center sm:px-5 sm:py-20 sm:pt-[100px] lg:px-8">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Get started
              </span>

              <h2 className="mt-8 text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-[2.75rem]">
                Join PhonebookPlus today
              </h2>
              <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-white/90 sm:text-lg">
                Create your free account and start building your smarter contact network in minutes.
              </p>

              <div className="mt-10 flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center">
                <Link
                  to="/sign-up"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-[#5b3fd4] shadow-[0_8px_32px_rgba(0,0,0,0.15)] transition hover:bg-violet-50 hover:shadow-xl sm:w-auto"
                >
                  Create Free Account
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/login"
                  className="inline-flex w-full items-center justify-center rounded-full border-2 border-white bg-white/10 px-8 py-4 text-sm font-bold text-white backdrop-blur-sm transition hover:bg-white/20 sm:w-auto"
                >
                  Sign In
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-white/80">
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Free to start
                </span>
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Web &amp; mobile
                </span>
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Global access
                </span>
              </div>
            </div>
          </div>

          <SiteFooter onSectionClick={scrollTo} />
        </section>
      </main>

      {/* Mobile dots */}
      <div className="fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 gap-2 rounded-full border border-violet-100 bg-white/90 px-3 py-2 shadow-lg backdrop-blur xl:hidden">
        {SECTIONS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Go to section ${i + 1}`}
            className={`rounded-full transition-all ${
              activeIndex === i ? "h-2 w-6 bg-violet-600" : "h-2 w-2 bg-violet-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
